import PageContainer from '@components/PageContainer';
import { useSendOtp } from '@hooks/postQueries';
import AuthFormLayout from '@layouts/form/AuthFormLayout';
import { Button, CircularProgress, InputLabel, TextField, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ForgetPassword = () => {
  const navigate = useNavigate();

  // Initialize useForm hook
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: '',
    },
  });

  // Validate Email format
  const validateEmail = (value) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(value) || 'Invalid email format';
  };

  const { mutateAsync, isPending, error } = useSendOtp();

  // Handle form submission
  const onSubmit = async (data) => {
    await mutateAsync(
      { email: data.email, purpose: 'forget_password' },
      {
        onSuccess: () => {
          navigate('/forget-password/send-otp', {
            state: { email: data.email, purpose: 'forget_password' },
          });
        },
        onError: (error) => {
          const errorMessage =
            error?.response?.data?.message || 'Failed to send OTP. Please try again.';
          toast.error(errorMessage);
        },
      },
    );
  };

  return (
    <PageContainer title="Qualz.ai | Forgot Password">
      <AuthFormLayout
        title={'Forgot your password?'}
        text="No worries! Enter your email address, and we’ll send you a link to reset your
                password."
      >
        {/* Form with react-hook-form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <InputLabel htmlFor="username" sx={{ mb: 1, fontWeight: '500' }}>
            Email
          </InputLabel>
          <TextField
            fullWidth
            placeholder="name@gmail.com"
            variant="outlined"
            {...register('email', {
              required: 'Email is required',
              validate: validateEmail,
            })}
            error={!!errors.email}
            helperText={errors.email?.message}
            sx={{ mb: 2 }}
          />

          {error && (
            <Typography variant="body1" color="error" sx={{ mb: 1 }}>
              {error?.response.data.detail}
            </Typography>
          )}
          <Button
            fullWidth
            variant="contained"
            size="large"
            type="submit"
            sx={{ mb: 2 }}
            disabled={isPending}
            aria-live="polite"
          >
            {isPending ? <CircularProgress size={24} color="inherit" /> : 'Continue'}
          </Button>
        </form>

        <Typography variant="body1" align="center" sx={{ mt: 1 }}>
          Remembered your password?{' '}
          <Link
            to="/signin"
            style={{
              color: 'primary.main',
              fontWeight: 'bold',
              '&:hover, &:focus': {
                textDecoration: 'none',
                outline: '2px solid',
                outlineColor: 'primary.main',

                borderRadius: '2px',
                outlineOffset: '2px',
              },
            }}
          >
            Sign in
          </Link>
        </Typography>
      </AuthFormLayout>
    </PageContainer>
  );
};

export default ForgetPassword;
